import { useEffect, useRef, useState } from 'react';

export default function Interests() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const interests = [
    {
      title: 'Space & Astronomy',
      image: 'https://public.readdy.ai/ai/img_res/aab1e48457f4b0493b598dc3e2085e2f.jpg',
    },
    {
      title: 'Fitness',
      image: 'https://readdy.ai/api/search-image?query=Person%20exercising%20gym%20workout%20fitness%20training%20dumbbells%20athletic%20active%20lifestyle%20healthy%20living%20real%20life%20candid%20moment%20strength%20training%20wellness&width=600&height=400&seq=interest-fitness-new&orientation=landscape',
    },
    {
      title: 'Travel',
      image: 'https://readdy.ai/api/search-image?query=Person%20with%20backpack%20exploring%20beautiful%20scenic%20destination%20travel%20adventure%20mountains%20nature%20outdoor%20hiking%20real%20life%20candid%20moment%20wanderlust%20journey%20exploration&width=400&height=600&seq=interest-travel-new&orientation=portrait',
    },
    {
      title: 'Badminton',
      image: 'https://readdy.ai/api/search-image?query=Person%20playing%20badminton%20indoor%20court%20racket%20shuttlecock%20sports%20action%20athletic%20moment%20real%20life%20candid%20shot%20competitive%20game%20active%20lifestyle&width=500&height=500&seq=interest-badminton&orientation=squarish',
    },
    {
      title: 'Cricket',
      image: 'https://readdy.ai/api/search-image?query=Person%20playing%20cricket%20bat%20ball%20outdoor%20field%20sports%20action%20athletic%20moment%20real%20life%20candid%20shot%20competitive%20game%20active%20lifestyle%20sunny%20day&width=400&height=600&seq=interest-cricket&orientation=portrait',
    },
    {
      title: 'Music',
      image: 'https://readdy.ai/api/search-image?query=Person%20with%20headphones%20enjoying%20music%20relaxed%20moment%20listening%20audio%20peaceful%20atmosphere%20real%20life%20candid%20shot%20music%20lover%20lifestyle%20aesthetic%20cozy&width=600&height=400&seq=interest-music-new&orientation=landscape',
    },
    {
      title: 'Coffee',
      image: 'https://readdy.ai/api/search-image?query=Hands%20holding%20artisan%20coffee%20cup%20latte%20art%20cozy%20cafe%20atmosphere%20warm%20lighting%20coffee%20culture%20lifestyle%20real%20life%20moment%20aesthetic%20coffee%20shop%20barista&width=400&height=300&seq=interest-coffee-new&orientation=landscape',
    },
  ];

  return (
    <section
      id="interests"
      ref={sectionRef}
      className="relative py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <span className="inline-block text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Beyond Design</span>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            My Interests
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            When I'm not designing, you'll find me exploring these passions
          </p>
        </div>

        {/* Collage Grid */}
        <div className="grid grid-cols-12 gap-4 max-w-6xl mx-auto">
          {/* Space & Astronomy - Extra Large Feature Card */}
          <div
            className={`col-span-12 md:col-span-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 -translate-x-20 scale-95'
            }`}
            style={{ transitionDelay: '0.1s' }}
          >
            <div className="group relative h-96 rounded-2xl overflow-hidden border-2 border-slate-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
                 onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
              <img
                src={interests[0].image}
                alt={interests[0].title}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white rounded-xl px-6 py-4 border-2 border-slate-200">
                <h3 className="text-3xl font-bold text-slate-900 mb-2">{interests[0].title}</h3>
                <div className="w-24 h-1 rounded-full" style={{ backgroundColor: '#6b3d52' }}></div>
              </div>
            </div>
          </div>

          {/* Fitness - Medium vertical */}
          <div
            className={`col-span-12 md:col-span-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-20 scale-95'
            }`}
            style={{ transitionDelay: '0.2s' }}
          >
            <div className="group relative h-96 rounded-2xl overflow-hidden border-2 border-slate-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
                 onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
              <img
                src={interests[1].image}
                alt={interests[1].title}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white rounded-xl px-5 py-3 border-2 border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{interests[1].title}</h3>
                <div className="w-16 h-1 rounded-full" style={{ backgroundColor: '#6b3d52' }}></div>
              </div>
            </div>
          </div>

          {/* Travel - Tall vertical */}
          <div
            className={`col-span-12 md:col-span-5 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-20 scale-95'
            }`}
            style={{ transitionDelay: '0.3s' }}
          >
            <div className="group relative h-80 rounded-2xl overflow-hidden border-2 border-slate-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
                 onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
              <img
                src={interests[2].image}
                alt={interests[2].title}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white rounded-xl px-5 py-3 border-2 border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{interests[2].title}</h3>
                <div className="w-16 h-1 rounded-full" style={{ backgroundColor: '#6b3d52' }}></div>
              </div>
            </div>
          </div>

          {/* Badminton - Square */}
          <div
            className={`col-span-12 md:col-span-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-20 scale-95'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
            <div className="group relative h-80 rounded-2xl overflow-hidden border-2 border-slate-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
                 onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
              <img
                src={interests[3].image}
                alt={interests[3].title}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white rounded-xl px-5 py-3 border-2 border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{interests[3].title}</h3>
                <div className="w-16 h-1 rounded-full" style={{ backgroundColor: '#6b3d52' }}></div>
              </div>
            </div>
          </div>

          {/* Cricket - Medium */}
          <div
            className={`col-span-12 md:col-span-3 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-20 scale-95'
            }`}
            style={{ transitionDelay: '0.5s' }}
          >
            <div className="group relative h-80 rounded-2xl overflow-hidden border-2 border-slate-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
                 onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
              <img
                src={interests[4].image}
                alt={interests[4].title}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white rounded-xl px-5 py-3 border-2 border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{interests[4].title}</h3>
                <div className="w-16 h-1 rounded-full" style={{ backgroundColor: '#6b3d52' }}></div>
              </div>
            </div>
          </div>

          {/* Music - Wide */}
          <div
            className={`col-span-12 md:col-span-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-20 scale-95'
            }`}
            style={{ transitionDelay: '0.6s' }}
          >
            <div className="group relative h-64 rounded-2xl overflow-hidden border-2 border-slate-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
                 onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
              <img
                src={interests[5].image}
                alt={interests[5].title}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white rounded-xl px-5 py-3 border-2 border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{interests[5].title}</h3>
                <div className="w-16 h-1 rounded-full" style={{ backgroundColor: '#6b3d52' }}></div>
              </div>
            </div>
          </div>

          {/* Coffee - Small */}
          <div
            className={`col-span-12 md:col-span-3 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-20 scale-95'
            }`}
            style={{ transitionDelay: '0.7s' }}
          >
            <div className="group relative h-64 rounded-2xl overflow-hidden border-2 border-slate-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
                 onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
              <img
                src={interests[6].image}
                alt={interests[6].title}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white rounded-xl px-5 py-3 border-2 border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{interests[6].title}</h3>
                <div className="w-12 h-1 rounded-full" style={{ backgroundColor: '#6b3d52' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
